import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Modal from '../../components/common/Modal';
import Loader from '../../components/common/Loader';
import { useAuth } from '../../context/AuthContext';

const emptyForm = { name: '', description: '', permissions: [] };

export default function Roles() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [permData, setPermData] = useState({ permissions: [], groups: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.roles.list({ per_page: 100 });
      const allRoles = Array.isArray(data) ? data : data.roles ? (data.roles.data || data.roles) : data.data || [];
      setRows(allRoles.filter((r) => (r.slug || '').toLowerCase() !== 'developer'));
    } catch (err) {
      showError('Failed to load roles', extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.permissions.list().then(({ data }) => setPermData(data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name,
      description: row.description || '',
      permissions: (row.permissions || []).map((p) => Number(typeof p === 'object' ? p.id : p)),
    });
    setModal(true);
  };

  const togglePerm = (id) => {
    const pid = Number(id);
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(pid)
        ? prev.permissions.filter((p) => p !== pid)
        : [...prev.permissions, pid],
    }));
  };

  const groupBy = (perms) =>
    permData.groups
      .map((g) => ({ group: g, perms: perms.filter((p) => p.group === g) }))
      .filter((g) => g.perms.length);

  const isAdminRole = (row) => {
    const slug = (row.slug || row.name || '').toLowerCase();
    return slug === 'admin' || slug === 'developer';
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.name) errs.name = 'Role name is required';
    if (!form.description) errs.description = 'Description is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.roles.update(editing.id, form);
        toast('success', '', 'Role updated successfully');
      } else {
        await api.roles.create(form);
        toast('success', '', 'Role created successfully');
      }
      setModal(false);
      load();
    } catch (err) {
      showError('Failed to save role', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const res = await confirmDelete(`role "${row.name}"`);
    if (!res.isConfirmed) return;
    try {
      await api.roles.remove(row.id);
      toast('success', '', 'Role deleted');
      load();
    } catch (err) {
      showError('Failed to delete role', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Roles & Permissions"
        subtitle="Define roles and assign granular permissions."
        crumbs={[{ label: 'Administration' }, { label: 'Roles' }]}
        actions={hasPermission('manage-roles') && (
          <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Add Role</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="roles" label="roles" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No roles found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'name', label: 'Role', render: (r) => <b>{r.name}</b> },
              { key: 'description', label: 'Description' },
              { key: 'permissions', label: 'Permissions', render: (r) => (
                <span className="badge badge-indigo">{r.permissions ? r.permissions.length : 0} permissions</span>
              ) },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {isAdminRole(r) ? (
                    <span className="badge badge-green">Protected</span>
                  ) : (
                    hasPermission('manage-roles') && (
                      <>
                        <button className="btn-icon" onClick={() => openEdit(r)}><FiEdit2 /></button>
                        <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                      </>
                    )
                  )}
                </div>
              ) },
            ]}
          />
        </div>
      </div>

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editing ? 'Edit Role' : 'Add Role'}
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update Role' : 'Create Role'}
            </button>
          </>
        }
      >
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Role Name <span className="req">*</span></label>
            <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} placeholder="e.g. Accountant" />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Description <span className="req">*</span></label>
            <input className={`form-control ${errors.description ? 'is-invalid' : ''}`} value={form.description} onChange={(e) => { setForm({ ...form, description: e.target.value }); if (errors.description) setErrors((p) => ({ ...p, description: '' })); }} placeholder="Short description" />
            {errors.description && <div className="invalid-feedback">{errors.description}</div>}
          </div>
        </div>

        {permData.permissions.length === 0 ? (
          <Loader text="Loading permissions..." />
        ) : (
          <div>
            <div className="section-title">Permissions</div>
            {groupBy(permData.permissions).map((g) => (
              <div key={g.group} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>
                  {g.group}
                </div>
                <div className="grid-3">
                  {g.perms.map((p) => (
                    <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.permissions.includes(Number(p.id))} onChange={() => togglePerm(p.id)} />
                      <span style={{ fontSize: 12 }}>{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}